import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Fixture, KickoffCell, MatchFlags, StatusPill } from '../components/match';
import { Topbar } from '../components/Shell';
import { Confirm, Empty, ErrorBox, Field, Loading, Modal, useAction } from '../components/ui';
import { del, get, patch, post, qs } from '../lib/api';
import { fmtDate, fmtDateTime, fromGmtInput, toGmtInput } from '../lib/format';
import type { GameSettings, MatchRow, Round } from '../lib/types';

export function SlatePage() {
  const search = useSearch({ strict: false }) as { round?: number };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<{ settings: GameSettings }>('/admin/settings') });
  const season = settings.data?.settings.currentSeason;
  const rounds = useQuery({
    queryKey: ['rounds', season],
    queryFn: () => get<{ rounds: Round[] }>(`/admin/rounds${qs({ season })}`),
    enabled: !!season,
  });
  const list = rounds.data?.rounds ?? [];
  // Default: the round running now, else the next one.
  const now = Date.now();
  const current =
    list.find((r) => new Date(r.startsAt).getTime() <= now && new Date(r.endsAt).getTime() > now) ??
    [...list].reverse().find((r) => new Date(r.startsAt).getTime() > now) ??
    list[0];
  const selectedId = search.round ?? current?.id;
  const selected = list.find((r) => r.id === selectedId);

  const matches = useQuery({
    queryKey: ['matches', { roundId: selectedId }],
    queryFn: () => get<{ matches: MatchRow[] }>(`/admin/matches${qs({ roundId: selectedId })}`),
    enabled: !!selectedId,
  });
  const [picked, setPicked] = useState<Set<number>>(new Set());
  useEffect(() => setPicked(new Set()), [selectedId]);
  const [roundModal, setRoundModal] = useState<'new' | 'edit' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { busy, run } = useAction();

  const ms = matches.data?.matches ?? [];
  const publishedCount = ms.filter((m) => m.published).length;
  const target = settings.data?.settings.slateTargetSize ?? 10;
  const setPublished = (ids: number[], published: boolean) =>
    run(async () => {
      await post('/admin/matches/publish', { ids, published });
      setPicked(new Set());
      await qc.invalidateQueries({ queryKey: ['matches'] });
      await qc.invalidateQueries({ queryKey: ['rounds'] });
    }, published ? `Published ${ids.length} match(es)` : `Unpublished ${ids.length} match(es)`);

  return (
    <>
      <Topbar
        title="Weekly slate"
        sub={`Season ${season ?? '…'} · pick which matches players predict each week`}
        actions={
          <button type="button" className="btn" onClick={() => setRoundModal('new')}>
            New round
          </button>
        }
      />
      <div className="page">
        {rounds.data && !list.some((r) => r.publishedCount > 0 && new Date(r.endsAt).getTime() > now) && (
          <div className="err-box" style={{ marginBottom: 16 }}>
            No week with published matches is coming up. Players see "No matches to predict right now" and the latest results until you publish the next week's
            matches (Data feed → import, or a new round here).
          </div>
        )}
        {rounds.error && <ErrorBox error={rounds.error} />}
        <div className="cols" style={{ gridTemplateColumns: '260px minmax(0, 1fr)' }}>
          <nav className="card" aria-label="Rounds">
            <div className="card-head">
              <h2>Rounds</h2>
            </div>
            {rounds.isLoading && <Loading />}
            {list.length === 0 && !rounds.isLoading && <Empty>No rounds yet. Creating a match creates its week's round.</Empty>}
            <ul style={{ listStyle: 'none', margin: 0, padding: 6 }}>
              {list.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ width: '100%', justifyContent: 'space-between', background: r.id === selectedId ? 'var(--green-soft)' : undefined }}
                    aria-current={r.id === selectedId ? 'true' : undefined}
                    onClick={() => navigate({ to: '/slate', search: { round: r.id } })}
                  >
                    <span>{r.name}</span>
                    <span className="muted small num">
                      {r.publishedCount}/{r.matchCount}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <section className="card" aria-labelledby="round-h">
            <div className="card-head">
              <div>
                <h2 id="round-h">{selected?.name ?? 'No round selected'}</h2>
                {selected && (
                  <span className="muted small">
                    {fmtDate(selected.startsAt)} to {fmtDate(selected.endsAt)} · {publishedCount} of {target} published
                    {!selected.opened && selected.opensAt && (
                      <span className="pill pill-amber" style={{ marginLeft: 8 }} title="Published matches stay hidden from players until the current week ends">
                        Opens to players {fmtDateTime(selected.opensAt)}
                      </span>
                    )}
                  </span>
                )}
              </div>
              {selected && (
                <div className="actions">
                  <button type="button" className="btn btn-sm" disabled={busy || picked.size === 0} onClick={() => setPublished([...picked], true)}>
                    Publish selected
                  </button>
                  <button type="button" className="btn btn-sm" disabled={busy || picked.size === 0} onClick={() => setPublished([...picked], false)}>
                    Unpublish selected
                  </button>
                  <button type="button" className="btn btn-sm" onClick={() => setRoundModal('edit')}>
                    Edit round
                  </button>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => setConfirmDelete(true)}>
                    Delete
                  </button>
                  <Link to="/matches/new" search={{ roundId: selected.id }} className="btn btn-sm btn-primary">
                    Add match
                  </Link>
                </div>
              )}
            </div>
            {matches.isLoading && <Loading />}
            {selected && ms.length === 0 && !matches.isLoading && <Empty>No matches in this round yet.</Empty>}
            {ms.length > 0 && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          aria-label="Select all"
                          checked={picked.size === ms.length}
                          onChange={(e) => setPicked(e.target.checked ? new Set(ms.map((m) => m.id)) : new Set())}
                        />
                      </th>
                      <th>Kickoff</th>
                      <th>Competition</th>
                      <th>Match</th>
                      <th>Status</th>
                      <th className="right">Picks</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {ms.map((m) => (
                      <tr key={m.id} className={m.published ? '' : 'dim'}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${m.homeName} v ${m.awayName}`}
                            checked={picked.has(m.id)}
                            onChange={(e) => {
                              const n = new Set(picked);
                              if (e.target.checked) n.add(m.id);
                              else n.delete(m.id);
                              setPicked(n);
                            }}
                          />
                        </td>
                        <td>
                          <KickoffCell m={m} />
                        </td>
                        <td className="muted">{m.competitionName}</td>
                        <td style={{ minWidth: 280 }}>
                          <Fixture m={m} />
                        </td>
                        <td>
                          <StatusPill m={m} />
                        </td>
                        <td className="right num">{m.predictionCount.toLocaleString()}</td>
                        <td className="right">
                          <MatchFlags m={m} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="card-foot">
              Draft matches are invisible to players. A “Time TBC” match is shown but stays closed until its kickoff is confirmed. A kickoff moved more
              than the configured hours later reopens predictions; a match that has started never reopens.
            </div>
          </section>
        </div>
      </div>

      {roundModal && (
        <RoundForm
          round={roundModal === 'edit' ? selected : undefined}
          season={season ?? ''}
          onClose={() => setRoundModal(null)}
          onSaved={async (id) => {
            await qc.invalidateQueries({ queryKey: ['rounds'] });
            navigate({ to: '/slate', search: { round: id } });
          }}
        />
      )}
      {confirmDelete && selected && (
        <Confirm
          title={`Delete ${selected.name}?`}
          body="Only an empty round can be deleted. Move or delete its matches first."
          confirmLabel="Delete round"
          danger
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await del(`/admin/rounds/${selected.id}`);
            await qc.invalidateQueries({ queryKey: ['rounds'] });
            navigate({ to: '/slate', search: {} });
          }}
        />
      )}
    </>
  );
}

export function RoundForm({ round, season, onClose, onSaved }: { round?: Round; season: string; onClose: () => void; onSaved: (id: number) => void }) {
  const [name, setName] = useState(round?.name ?? '');
  const [roundSeason, setSeason] = useState(round?.season ?? season);
  const [startsAt, setStartsAt] = useState(toGmtInput(round?.startsAt));
  const [endsAt, setEndsAt] = useState(toGmtInput(round?.endsAt));
  const { busy, run } = useAction();
  return (
    <Modal
      title={round ? `Edit ${round.name}` : 'New round'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() =>
              run(async () => {
                const body = { name, season: roundSeason, startsAt: fromGmtInput(startsAt), endsAt: fromGmtInput(endsAt) };
                const r = round ? await patch<{ round: Round }>(`/admin/rounds/${round.id}`, body) : await post<{ round: Round }>('/admin/rounds', body);
                onSaved(r.round.id);
                onClose();
              }, round ? 'Round saved' : 'Round created')
            }
          >
            {round ? 'Save' : 'Create round'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Name" hint='e.g. "Week 43"'>
          <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Season">
          <input className="input" value={roundSeason} onChange={(e) => setSeason(e.target.value)} />
        </Field>
        <Field label="Starts (GMT)">
          <input className="input" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </Field>
        <Field label="Ends (GMT)" hint="The next week opens to players at 00:00 GMT the day after">
          <input className="input" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
